/** @format */

import type {Props as StatusIndicatorProps} from '@src/components/tailwind/StatusIndicator'

import {EMPTY_KEY, THIRTY_DAYS_MS} from './constants'
import type {SubmissionsFolder, SubmissionStatus} from './SubmissionsContext'

/** @format */

const compactFormatOptions: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  hour12: false,
  minute: 'numeric',
}

const fullFormatOptions: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  hour12: false,
  minute: 'numeric',
}

function formatWithOptions(
  dateStr: string,
  options: Intl.DateTimeFormatOptions,
  timeZone?: string,
): string {
  const timestamp = Date.parse(dateStr)
  if (isNaN(timestamp)) {
    throw new Error(`Invalid date string: ${dateStr}`)
  }

  const date = new Date(timestamp)
  return new Intl.DateTimeFormat('en-US', {
    ...options,
    ...(timeZone ? {timeZone} : {}),
  }).format(date)
}

export function formatDateFull(dateStr: string, timeZone?: string): string {
  return formatWithOptions(dateStr, fullFormatOptions, timeZone)
}

export function formatDateCompact(dateStr: string, timeZone?: string): string {
  return formatWithOptions(dateStr, compactFormatOptions, timeZone)
}

/**
 * Formats a date string differently based on whether it's within the last 30 days
 *
 * @param dateStr - ISO 8601 date string
 * @param timeZone - Explicit timezone to format in (defaults to viewer's timezone)
 * @returns Formatted date string using either reduced or full format
 * @throws Error if the date string is invalid
 */
export function formatDate(dateStr: string, timeZone?: string): string {
  const timestamp = Date.parse(dateStr)
  if (isNaN(timestamp)) {
    throw new Error(`Invalid date string: ${dateStr}`)
  }

  return Date.now() - timestamp < THIRTY_DAYS_MS
    ? formatDateCompact(dateStr, timeZone)
    : formatDateFull(dateStr, timeZone)
}

const WHITESPACE = /\s/g

/**
 * Generates a CSS variable name for column size based on the provided field name.
 *
 * getColumnSizeVar replaces any whitespace in the field name with underscores
 * and converts the field name to lowercase to create a valid CSS variable name
 * for use in styles.
 *
 * @param {string} field - The field name (can contain whitespace) to generate a CSS variable name.
 * @returns {string} The generated CSS variable name for column size.
 */
export function getColumnSizeVar(field: string): string {
  const sanitizedField = field.toLowerCase().replace(WHITESPACE, '_')
  return `--col-size-${sanitizedField}`
}

export function getFileName(fileUrl: string): string {
  try {
    const {pathname} = new URL(fileUrl)
    // Extract the last segment of the pathname after the last "/"
    const fileName = pathname.split('/').pop()
    return fileName ?? fileUrl // Fallback to the original URL if no match
  } catch {
    return fileUrl
  }
}

// Handle cases where the field name is an empty string (`""`),
// since it breaks @tanstack/react-table.
export function sanitizeFieldName(field: string): string {
  return field === '' ? EMPTY_KEY : field
}

const statusSignificance = [
  'open',
  'delivered',
  'processed',
  'dispatched',
  'pending',
  'processing',
]

export function getMostSignificantStatus(
  status: SubmissionStatus[string],
): string | undefined {
  if (status.error) {
    return 'error'
  }
  return statusSignificance.find(key => key in status)
}

// Received past the plan's monthly limit and held back until reprocessed. The
// API sends such a row with every column cut short.
export function isOverlimitSubmission(status: SubmissionStatus): boolean {
  return status['']?.error_code === 'OVER_LIMIT'
}

export function getStatusIndicatorType(
  status: SubmissionStatus,
): StatusIndicatorProps['type'] | null {
  if (Object.keys(status).length === 0) {
    return null
  }

  let result: StatusIndicatorProps['type'] = 'ok'
  for (const [k, v] of Object.entries(status)) {
    // Initially we didn't have `status` in the API response, so we need to
    // handle the case where status has error attr without a `status` property.
    if (v.message) {
      result = 'warning'
      continue
    }

    if (k === 'error' || (v.error && (!v.status || v.status === 'processed'))) {
      return 'error'
    }

    const isPlugin = k.startsWith('plugin:')

    if (
      isPlugin
        ? v.status === 'processing' ||
          v.status === 'reprocessing' ||
          v.status == 'retrying'
        : v.status !== 'processed' &&
          v.status !== 'dispatched' &&
          v.status !== 'delivered' &&
          v.status !== 'open' &&
          // old status system
          !v.processed &&
          !v.dispatched &&
          !v.delivered &&
          !v.open
    ) {
      result = 'warning'
    }
  }

  return result
}

type SelectAllMatchingArgs = {
  folder: SubmissionsFolder
  loadedCount: number
  matchingCount: number
  selectedCount: number
}

/**
 * Whether to offer the Gmail-style "Select all N over-limit submissions"
 * link: only in the over-limit folder, when every loaded row is selected
 * and there are more matching submissions beyond the loaded page.
 */
export function canOfferSelectAllMatching(
  args: SelectAllMatchingArgs,
): boolean {
  const {folder, loadedCount, matchingCount, selectedCount} = args
  return (
    folder === 'overlimit' &&
    selectedCount > 0 &&
    selectedCount === loadedCount &&
    matchingCount > loadedCount
  )
}

type AcceptedOverlimitArgs = {
  // how many of the requested submissions the server said fit in the limit
  count: number
  loadedIds: number[]
  requestedCount: number
  selectAllMatching: boolean
  selectedIds: number[]
}

/**
 * Which rows to drop from the table right after a reprocess request, before
 * the async worker has run. Only when everything requested fits: the worker
 * takes rows in id order but skips any that turned spam, were deleted, or
 * were claimed by an overlapping task, so on a partial fit we can't know
 * which rows it will process and leave them all to the reconciling refetch.
 */
export function getAcceptedOverlimitIds(args: AcceptedOverlimitArgs): number[] {
  const {count, loadedIds, requestedCount, selectAllMatching, selectedIds} =
    args
  if (count < requestedCount) {
    return []
  }
  return selectAllMatching ? loadedIds : selectedIds
}

// refetch schedule after a reprocess request: the worker runs async and a
// large batch outlives any single delay, so keep checking with backoff
export const OVERLIMIT_RECONCILE_DELAYS_MS = [2000, 5000, 10000, 20000]

export function scheduleOverlimitReconcile(
  revalidate: () => Promise<unknown>,
  delays: readonly number[] = OVERLIMIT_RECONCILE_DELAYS_MS,
): void {
  for (const delay of delays) {
    setTimeout(() => void revalidate(), delay)
  }
}

// the API's expected quota refusal, as thrown by useFetch (the JSON error
// body is merged into the Error)
export function isOverLimitNoRoomError(err: unknown): err is Error {
  return (
    err instanceof Error && 'code' in err && err.code === 'OVER_LIMIT_NO_ROOM'
  )
}

export type MonthlyQuota = {
  // ISO date the monthly counters roll over on
  limitResetsOn: string
  // null means no finite limit
  monthlyLimit: number | null
  // room left in the limit this month; null means no finite limit
  remaining: number | null
}

// "Oct 1": the reset date is a calendar day, so format it in UTC to keep it
// from slipping to the previous evening in western timezones
// The noun that agrees with `count`: "submission" for 1, "submissions" for
// anything else. The caller prints the number, which often needs its own markup.
export function pluralize(count: number, singular: string): string {
  return count === 1 ? singular : `${singular}s`
}

export function formatResetDate(dateStr: string): string {
  return formatWithOptions(dateStr, {month: 'short', day: 'numeric'}, 'UTC')
}
